import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useSimulationsByContactIds } from "../../../hooks/feeLab/useSimulation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { simulationService } from "../../../services/SimulationService";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { formatIsoDateWithTime } from "../../../utils/namesFormater";
import { formatSimulationStatus, colorSimulationStatus } from "../../../utils/simulationUtil";
import { Simulation, SimulationStatus } from "../../../interfaces/DTO/simulation";
import PersonIcon from "@mui/icons-material/Person";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import EditNoteIcon from "@mui/icons-material/EditNote";
import UpdateIcon from "@mui/icons-material/Update";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useFamilies } from "../../../hooks/crm/useFamilies";
import SaveSimulationDialog from "./SaveSimulationDialog";
import { SnackbarType, useSnackbarStore } from "../../../stores/useSnackbarStore";

interface Props {
  selectedPeriod: string;
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
  contacts: Contact[];
  familyId?: number;
  onSelect: (simulation: Simulation) => void;
  onCompare?: (simulation: Simulation) => void;
}

// Returns true when two "YYYYMM" period strings are within 3 calendar months of each other
function isWithinThreeMonths(a: string, b: string): boolean {
  const aYear = parseInt(a.slice(0, 4), 10);
  const aMonth = parseInt(a.slice(4, 6), 10);
  const bYear = parseInt(b.slice(0, 4), 10);
  const bMonth = parseInt(b.slice(4, 6), 10);
  const diff = Math.abs((aYear * 12 + aMonth) - (bYear * 12 + bMonth));
  return diff <= 3;
}

const SimulationPickerList: React.FC<Props> = ({ selectedPeriod, selectedPeriodType, contacts, familyId, onSelect, onCompare }) => {
  const contactIds = contacts.map((c) => c.id);

  const { data: simulations = [], isLoading } = useSimulationsByContactIds(contactIds);
  const { data: families = [], isLoading: loadingFamilies } = useFamilies();
  const [comparisonSimulation, setComparisonSimulation] = useState<Simulation | null>(null);
  const [simToDelete, setSimToDelete] = useState<Simulation | null>(null);
  const [hoveredSimId, setHoveredSimId] = useState<number | null>(null);
  const [editSim, setEditSim] = useState<Simulation | null>(null);
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const { mutate: deleteSimulation, isPending: isDeleting } = useMutation({
    mutationFn: (id: number) => simulationService.deleteWithItems(id),
  });
  const queryClient = useQueryClient();

  const handleDeleteConfirm = () => {
    if (!simToDelete) return;
    deleteSimulation(simToDelete.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["simulations-by-users"] });
        setSimToDelete(null);
      },
    });
  };

  const handleMetadataUpdate = async (name: string, notes: string, status: SimulationStatus) => {
    if (!editSim?.id) return;
    try {
      await simulationService.patchMetadata(editSim.id, { name, notes, status });
      queryClient.invalidateQueries({ queryKey: ["simulations-by-users"] });
      showSnackbar("סימולציה עודכנה בהצלחה", SnackbarType.SUCCESS);
    } catch (e: any) {
      showSnackbar("משהו השתבש. לא עודכנה סימולציה", SnackbarType.ERROR);
      throw e;
    }
  };

  // מיפוי של contact_id לשם הלקוח
  const contactMap = useMemo(() => {
    return contacts.reduce<Record<number, string>>((acc, contact) => {
      acc[contact.id] = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
      return acc;
    }, {});
  }, [contacts]);

  // מיפוי של family_id לשם משפחה
  const familyMap = useMemo(() => {
    return families.reduce<Record<number, string>>((acc, family) => {
      acc[family.id] = family.name;
      return acc;
    }, {});
  }, [families]);

  // קיבוץ לפי reported_period (כבר ממויין בסדר יורד)
  const groupedByPeriod = useMemo(() => {
    const grouped: Record<string, Simulation[]> = {};
    simulations.forEach((sim) => {
      if (!grouped[sim.reported_period]) {
        grouped[sim.reported_period] = [];
      }
      grouped[sim.reported_period].push(sim);
    });
    return grouped;
  }, [simulations]);

  if (isLoading || loadingFamilies) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (simulations.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          אין סימולציות זמינות
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", direction: "rtl" }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        בחירת סימולציה
      </Typography>

      {Object.entries(groupedByPeriod).map(([period, sims]) => (
        <Box key={period} sx={{ mb: 4 }}>
          {/* כותרת התקופה */}
          <Box
            sx={{
              bgcolor: period === selectedPeriod ? "primary.main" : "grey.200",
              color: period === selectedPeriod ? "white" : "text.primary",
              px: 2,
              py: 1,
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              תקופה מדווחת: {period}
              {period === selectedPeriod && (
                <Chip
                  label="תקופה נוכחית"
                  size="small"
                  sx={{ mr: 2, bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                />
              )}
            </Typography>
          </Box>

          {/* רשימת הסימולציות */}
          <Stack spacing={2}>
            {sims.map((sim) => (
              <Card
                key={sim.id}
                variant="outlined"
                onMouseEnter={() => setHoveredSimId(sim.id)}
                onMouseLeave={() => setHoveredSimId(null)}
                sx={{
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: 3,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" position="relative">
                    {/* תוכן עיקרי */}
                    <Box sx={{ flex: 1 }}>
                      {/* שורה ראשונה - שם וסוג */}
                      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                        <Typography variant="h6" component="div">
                          {sim.name || "ללא שם"}
                        </Typography>
                        {sim.is_pre_advice && (
                          <Chip label="טרום ייעוץ" size="small" color="info" />
                        )}
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Chip
                            label={formatSimulationStatus(sim.status)}
                            size="small"
                            color={colorSimulationStatus(sim.status)}
                          />
                          {hoveredSimId === sim.id && (
                            <Tooltip title="ערוך פרטי סימולציה">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setEditSim(sim); }}
                                sx={{ p: 0.25 }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {sim.selected && (
                          <Chip label="נבחר" size="small" color="success" />
                        )}
                      </Stack>

                      {/* שורה שנייה - לקוח או משפחה */}
                      <Stack direction="row" spacing={3} mb={1.5} flexWrap="wrap">
                        {sim.family_id ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <FamilyRestroomIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              משפחת {familyMap[sim.family_id] || `משפחה ${sim.family_id}`}
                            </Typography>
                          </Box>
                        ) : sim.contact_id ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {contactMap[sim.contact_id] || "לא ידוע"}
                            </Typography>
                          </Box>
                        ) : null}
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      {/* שורה שלישית - פרטים נוספים */}
                      <Stack direction="row" spacing={3} mb={1} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <EditNoteIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            פוליסות ששונו: {sim.changed_policies_count}
                          </Typography>
                        </Box>
                        {sim.updated_at && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <UpdateIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              עודכן: {formatIsoDateWithTime(sim.updated_at)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      {/* הערות */}
                      {sim.notes && (
                        <Box
                          sx={{
                            bgcolor: "grey.50",
                            p: 1,
                            borderRadius: 1,
                            mt: 1.5,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            <strong>הערות:</strong> {sim.notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* כפתורי פעולה */}
                    <Stack spacing={1}>
                      {(() => {
                        const isCurrentPeriod = period === selectedPeriod;
                        const isPreAdviceMatch =
                          sim.is_pre_advice &&
                          selectedPeriodType === "PRE_ADVICE" &&
                          isWithinThreeMonths(period, selectedPeriod);
                        const canSelect = isCurrentPeriod || isPreAdviceMatch;
                        const isFamilySimInUserView = !familyId && !!sim.family_id;
                        return canSelect ? (
                          <Button
                            variant="contained"
                            size="medium"
                            onClick={() => !isFamilySimInUserView && onSelect(sim)}
                            disabled={isFamilySimInUserView}
                            sx={{ minWidth: 100 }}
                          >
                            בחר
                          </Button>
                        ) : (
                          onCompare && (
                            <Button
                              variant="outlined"
                              size="medium"
                              startIcon={<CompareArrowsIcon />}
                              onClick={() => onCompare(sim)}
                              sx={{ minWidth: 140 }}
                            >
                              השוואה
                            </Button>
                          )
                        );
                      })()}
                    </Stack>
                  </Stack>
                  <Box sx={{ position: "absolute", bottom: 8, left: 8 }}>
                    <IconButton size="small" onClick={() => setSimToDelete(sim)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      ))}

      <SaveSimulationDialog
        open={!!editSim}
        onClose={() => setEditSim(null)}
        existingSimulation={editSim}
        metadataOnly
        onSaveNew={async () => {}}
        onUpdateSimulation={handleMetadataUpdate}
      />

      <Dialog open={!!simToDelete} onClose={() => setSimToDelete(null)} dir="rtl">
        <DialogTitle>מחיקת סימולציה</DialogTitle>
        <DialogContent>
          <DialogContentText>
            האם למחוק את הסימולציה "{simToDelete?.name || "ללא שם"}"?
            פעולה זו תמחק את הסימולציה ואת כל הנתונים שלה לצמיתות.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimToDelete(null)}>ביטול</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting} variant="contained">
            {isDeleting ? "מוחק..." : "מחק"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SimulationPickerList;